import { useEffect, useRef } from 'react';
import { useFormContext, useFieldArray, useWatch } from 'react-hook-form';
import { Plus } from 'lucide-react';
import { Button } from '@vnetwork/ui/primitives';
import { EmptyState } from '@vnetwork/ui/data';
import { SectionShell } from './section-shell';
import { ProductLine } from './product-line';
import { emptyProduct, type ContractFormValues } from './contract-form';

/**
 * Order products section — a `useFieldArray` of ≥1 product lines. Once a
 * customer is chosen it auto-appends one empty line (legacy `hasAutoAdded`
 * behaviour), seeding each line's service-time dates from the contract term.
 * Adding/removing lines is manual; the per-type deep config is a later phase.
 */
export function ProductsSection({ customerId }: { customerId: number }) {
  const { control, formState } = useFormContext<ContractFormValues>();
  const { fields, append, remove } = useFieldArray({ control, name: 'products' });
  const effective = useWatch({ control, name: 'contract_effective_date' });
  const expiry = useWatch({ control, name: 'contract_expiry_date' });
  const arrayError = (formState.errors.products as { message?: string } | undefined)?.message;

  // Auto-append one empty line the first time a customer is selected.
  const autoAdded = useRef(false);
  useEffect(() => {
    if (!customerId) {
      autoAdded.current = false;
      return;
    }
    if (!autoAdded.current && fields.length === 0) {
      autoAdded.current = true;
      append(emptyProduct(effective, expiry));
    }
  }, [customerId, fields.length, append, effective, expiry]);

  const disabled = !customerId;

  return (
    <SectionShell
      title="Products & services"
      description="Add the services this contract covers."
      actions={
        <Button
          variant="secondary"
          size="sm"
          startIcon={<Plus size={15} />}
          disabled={disabled}
          onClick={() => append(emptyProduct(effective, expiry))}
        >
          Add product
        </Button>
      }
    >
      {disabled ? (
        <EmptyState title="No products yet" description="Select a customer to add products." />
      ) : (
        <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          {fields.map((f, i) => (
            <ProductLine
              key={f.id}
              index={i}
              customerId={customerId}
              canRemove={fields.length > 1}
              onRemove={() => remove(i)}
            />
          ))}
        </div>
      )}

      {arrayError ? (
        <span style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-danger-text)' }}>
          {arrayError}
        </span>
      ) : null}
    </SectionShell>
  );
}
